"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, EyeOff } from "lucide-react";
import useLogin from "@/components/hooks/login/useLogin.hook";
import { useTooltipFade } from "@/components/hooks/useTooltipFade.hook";
import CustomImage from "./features/shared/custom-image/custom-image";
import { Svg } from "image-config";
import Link from "next/link";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import EyeOpenIcon from "./ui/icons/EyeOpenIcon";
import EyeCloseIcon from "./ui/icons/EyeCloseIcon";

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginFormProps {
  // No props needed - all logic is handled internally
}

export function LoginForm({}: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const {
    form,
    onSubmit,
    loginLoading,
    showPassword: hookShowPassword,
    togglePasswordVisibility,
    handleGoogleLogin,
    googleLoading,
  } = useLogin();

  const {
    showTooltip: showEmailTooltip,
    handleMouseEnter: handleEmailMouseEnter,
    handleMouseLeave: handleEmailMouseLeave,
    handleTooltipOpenChange: handleEmailTooltipOpenChange,
    hideTooltip: hideEmailTooltip,
  } = useTooltipFade({
    delay: 2000,
    shouldShow: (value: string) => !value || value.trim() === "",
  });

  const {
    showTooltip: showPasswordTooltip,
    handleMouseEnter: handlePasswordMouseEnter,
    handleMouseLeave: handlePasswordMouseLeave,
    handleTooltipOpenChange: handlePasswordTooltipOpenChange,
    hideTooltip: hidePasswordTooltip,
  } = useTooltipFade({
    delay: 2000,
    shouldShow: (value: string) => !value || value.trim() === "",
  });
  return (
    <div className="">
      <Card
        className="w-full md:w-[440px] mx-auto border-border shadow-none"
        data-testid="login-card"
      >
        <CardContent className="md:p-8 p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <h1
              className="lg:text-[26px] text-[20px] leading-8 font-semibold text-foreground mb-2 text-balance leading-tight"
              data-testid="login-title"
            >
              First step to your dream <br /> home with Plana
            </h1>
            <p className="text-muted-foreground" data-testid="login-subtitle">
              Login to your account
            </p>
          </div>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              data-testid="login-form"
            >
              {/* Email Field */}

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="email" className="sr-only">
                      Email
                    </FormLabel>
                    <FormControl>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip
                          open={showEmailTooltip}
                          onOpenChange={handleEmailTooltipOpenChange}
                        >
                          <TooltipTrigger asChild>
                            <Input
                              id="email"
                              placeholder="Email"
                              {...field}
                              className="border-input"
                              data-testid="login-input-email"
                              onMouseEnter={() => handleEmailMouseEnter(field.value || "")}
                              onMouseLeave={handleEmailMouseLeave}
                              onChange={(e) => {
                                field.onChange(e);
                                // Hide tooltip immediately when user starts typing
                                hideEmailTooltip();
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            align="end"
                            className="bg-gray-800 text-white animate-in fade-in-0 duration-300 slow-fade-out p-2 h-6"
                            data-testid="login-email-tooltip"
                          >
                            <p className="text-xs leading-2">Please fill out this field</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Password Field */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="password" className="sr-only">
                      Password
                    </FormLabel>
                    <FormControl>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip
                          open={showPasswordTooltip}
                          onOpenChange={handlePasswordTooltipOpenChange}
                        >
                          <TooltipTrigger asChild>
                            <div className="relative">
                              <Input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                {...field}
                                placeholder="Password"
                                className="border-input pr-10"
                                aria-describedby="password-error"
                                data-testid="login-input-password"
                                onMouseEnter={() =>
                                  handlePasswordMouseEnter(field.value || "")
                                }
                                onMouseLeave={handlePasswordMouseLeave}
                                onChange={(e) => {
                                  field.onChange(e);
                                  // Hide tooltip immediately when user starts typing
                                  hidePasswordTooltip();
                                }}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-[26px] w-[26px] p-0 flex items-center justify-center !rounded-lg"
                                onClick={() => {
                                  setShowPassword(!showPassword);
                                }}
                                aria-label={
                                  showPassword ? "Hide password" : "Show password"
                                }
                                data-testid="login-toggle-password"
                              >
                                {showPassword ? (
                                  <EyeCloseIcon className=" " />
                                ) : (
                                  <EyeOpenIcon className=" " />
                                )}
                                <span className="sr-only">
                                  {showPassword
                                    ? "Hide password"
                                    : "Show password"}
                                </span>
                              </Button>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            align="end"
                            className="bg-gray-800 text-white animate-in fade-in-0 duration-300 slow-fade-out p-2 h-6"
                            data-testid="login-password-tooltip"
                          >
                            <p className="text-xs leading-2">
                              Please fill out this field
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Login Button */}
              <Button
                type="submit"
                className="w-full h-[42px]"
                disabled={loginLoading}
                data-testid="login-submit-button"
              >
                {loginLoading ? "Logging in..." : "Login"}
              </Button>

              {/* Divider */}
              <div className="relative md:mt-3 mt-1" data-testid="login-divider">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-background px-2 text-muted-foreground">
                    OR
                  </span>
                </div>
              </div>

              {/* Google Login Button */}

              <Button
                type="button"
                variant="outline"
                className="w-full h-[42px] bg-transparent md:mt-2 mt-1"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                data-testid="login-google-button"
              >
                <CustomImage
                  src={Svg.Google}
                  alt="Google"
                  width={28}
                  height={28}
                  className="mr-2"
                />
                {googleLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Redirecting to Google...
                  </div>
                ) : (
                  "Login with Google"
                )}
              </Button>
            </form>
          </Form>{" "}
          {/* Forgot Password Link */}
          <div className="text-center md:mt-6 mt-2">
            <Link
              href="/password/forgot"
              className="text-[14px] font-semibold text-[oklch(0.67_0.15_238.95)] hover:underline"
              data-testid="login-forgot-password-link"
            >
              Forgot your password?
            </Link>
          </div>
          {/* Sign Up Link */}
          <div className="text-center mt-2">
            <span className="text-[14px] font-normal text-muted-foreground">
              Don&apos;t have an account?{" "}
              <a
                href="/signup"
                className="text-[oklch(0.67_0.15_238.95)] hover:underline"
                data-testid="login-signup-link"
              >
                Sign up
              </a>
            </span>
          </div>
          {/* Terms and Privacy */}
        </CardContent>
      </Card>
      <div className="text-center md:mt-6 mt-2" data-testid="login-terms">
        <p className="text-[12px] font-normal text-muted-foreground">
          By signing in, you agree to our{" "}
          <a
            href="/terms-and-conditions"
            className="text-[oklch(0.67_0.15_238.95)] hover:underline"
            target="_blank"
            data-testid="login-terms-link"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/privacy-policy"
            className="text-[oklch(0.67_0.15_238.95)] hover:underline"
            target="_blank"
            data-testid="login-privacy-link"
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}
